import React, { useEffect, useState } from 'react';
import { useLocation, Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';

const BookingSuccessPage = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [bookingDetails, setBookingDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get booking reference from state or URL
  const bookingReference = location.state?.bookingReference || searchParams.get('ref');

  useEffect(() => {
    if (bookingReference) {
      fetchBookingDetails();
    } else {
      // Try to get from session_id
      const sessionId = searchParams.get('session_id');
      if (sessionId) {
        checkPaymentAndGetBooking(sessionId);
      } else {
        setLoading(false);
      }
    }
  }, [bookingReference]);

  const checkPaymentAndGetBooking = async (sessionId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/v1/checkout/status/${sessionId}`);
      const data = await response.json();
      
      if (data.payment_status === 'paid' && data.booking_reference) {
        // Fetch full booking details
        const bookingResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bookings/receipt/${data.booking_reference}`);
        const bookingData = await bookingResponse.json();
        
        if (bookingData.success) {
          setBookingDetails(bookingData.booking);
        }
      }
    } catch (err) {
      console.error('Error checking payment:', err);
      setError('Unable to verify payment status');
    } finally {
      setLoading(false);
    }
  };

  const fetchBookingDetails = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bookings/receipt/${bookingReference}`);
      const data = await response.json();
      
      if (data.success) {
        setBookingDetails(data.booking);
      } else {
        setError('Booking not found');
      }
    } catch (err) {
      console.error('Error fetching booking:', err);
      setError('Unable to load booking details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen overflow-x-hidden bg-gradient-to-b from-slate-50 to-white pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coral-500 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading booking details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-b from-slate-50 to-white pt-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-8 text-center"
        >
          {/* Success Icon */}
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <h1 className="text-3xl font-bold text-slate-800 mb-4">
            Booking Confirmed!
          </h1>

          <p className="text-lg text-slate-600 mb-8">
            Thank you for booking your adventure with Dive Camp Adventures!
          </p>

          {bookingDetails ? (
            <div className="bg-slate-50 rounded-xl p-6 text-left mb-8">
              <h2 className="text-xl font-semibold text-slate-800 mb-4">Booking Details</h2>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-600">Booking Reference:</span>
                  <span className="font-semibold text-coral-600">{bookingDetails.booking_reference}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-slate-600">Adventure:</span>
                  <span className="font-medium">{bookingDetails.adventure_title}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-slate-600">Date:</span>
                  <span className="font-medium">
                    {new Date(bookingDetails.booking_date + 'T00:00:00').toLocaleDateString('en-AU', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      timeZone: 'Australia/Sydney'
                    })}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-slate-600">Participants:</span>
                  <span className="font-medium">{bookingDetails.participants} {bookingDetails.participants === 1 ? 'person' : 'people'}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-slate-600">Total Paid:</span>
                  <span className="font-semibold text-green-600">${bookingDetails.total_price} AUD</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-slate-600">Status:</span>
                  <span className="font-medium text-green-600 capitalize">{bookingDetails.booking_status || 'Confirmed'}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-yellow-50 rounded-xl p-6 mb-8">
              <p className="text-yellow-800">
                {error || 'Your booking has been processed. Please check your email for confirmation details.'}
              </p>
            </div>
          )}

          {/* Next Steps */}
          <div className="bg-ocean-50 rounded-xl p-6 text-left mb-8">
            <h3 className="text-lg font-semibold text-ocean-800 mb-3">What's Next?</h3>
            <ul className="space-y-2 text-ocean-700">
              <li className="flex items-start">
                <span className="text-ocean-500 mr-2">✓</span>
                Check your email for booking confirmation and receipt
              </li>
              <li className="flex items-start">
                <span className="text-ocean-500 mr-2">✓</span>
                Complete your customer profile with sizing and dietary information
              </li>
              <li className="flex items-start">
                <span className="text-ocean-500 mr-2">✓</span>
                Ahmed will contact you before your adventure with meeting details
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {bookingDetails && (
              <Link
                to={`/customer-details/${bookingDetails.booking_reference}`}
                className="bg-coral-500 hover:bg-coral-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                Complete Your Profile
              </Link>
            )}
            <Link
              to="/"
              className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Return to Home
            </Link>
          </div>

          {/* Contact Info */}
          <div className="mt-8 pt-6 border-t border-slate-200">
            <p className="text-sm text-slate-500">
              Questions about your booking? Contact us at{' '}
              <a href="mailto:DiveCampAdventures@gmail.com" className="text-coral-600 hover:underline">
                DiveCampAdventures@gmail.com
              </a>
              {' '}or WhatsApp{' '}
              <a href="https://wa.me/61414710541" className="text-coral-600 hover:underline">
                +61 414 710 541
              </a>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default BookingSuccessPage;
